import React from "react";
import {
  FaCopy,
  FaEdit,
  FaEnvelope,
  FaGlobe,
  FaPhone,
  FaTrash,
  FaUser,
} from "react-icons/fa";
import { fmtDateFR, parseEmails, toAbsolute } from "../utils";
import { statutRowClasses } from "../constants";
import InfoTag from "./InfoTag";

export default function ProspectionTable({
  loading,
  rows,
  sort,
  setSort,
  onEdit,
  onDelete,
  onQuickView,
  onStatusChange,
  selectedIds,
  setSelectedIds,
  onQuickCopy,
}) {
  if (loading) return <SkeletonTable />;

  const toggleAll = (e) => {
    if (e.target.checked) {
      const ids = new Set(rows.map((r) => r.id));
      setSelectedIds(ids);
    } else {
      setSelectedIds(new Set());
    }
  };

  const toggleOne = (id) => {
    const next = new Set(selectedIds);

    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }

    setSelectedIds(next);
  };

  const th = (label, key) => (
    <th
      className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-300 cursor-pointer select-none"
      onClick={() => {
        if (!key) return;

        if (sort === key) {
          setSort(`-${key}`);
        } else if (sort === `-${key}`) {
          setSort("-updated_at");
        } else {
          setSort(key);
        }
      }}
      title="Trier"
    >
      {label}
    </th>
  );

  return (
    <div className="relative overflow-x-auto">
      <table className="table-auto w-max min-w-[1100px]">
        <thead className="bg-slate-50 dark:bg-slate-900/80">
          <tr>
            <th className="px-3 py-2">
              <input
                type="checkbox"
                aria-label="Tout sélectionner"
                checked={selectedIds.size === rows.length && rows.length > 0}
                onChange={toggleAll}
              />
            </th>

            {th("Club", "nom_club")}
            {th("Discipline", "discipline")}
            {th("Dept", "departement_code")}
            {th("Nb lic.", "nb_licencies")}
            {th("Coord.", null)}
            {th("Statut", "statut")}
            {th("Contacté", "date_contact")}
            {th("Site", null)}

            <th className="px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide">
              Actions
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.map((r) => {
            const emails = parseEmails(r.email);
            const quickCopyDisabled =
              emails.length === 0 || r._isUnsub || r.statut === "Désinscrit";

            return (
              <tr
                key={r.id}
                className={`border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50/60 dark:hover:bg-slate-800/40 ${statutRowClasses(
                  r.statut,
                )} ${r._isUnsub ? "bg-rose-50/50 dark:bg-rose-900/10" : ""}`}
              >
                <td className="px-3 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(r.id)}
                    onChange={() => toggleOne(r.id)}
                    className="h-6 w-6 accent-violet-600"
                  />
                </td>

                <td className="px-3 py-3 max-w-[260px]">
                  <button
                    type="button"
                    className="font-semibold text-slate-900 dark:text-slate-100 hover:underline block text-left"
                    onClick={() => onQuickView(r)}
                    title="Aperçu"
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {r.nom_club || "—"}
                  </button>

                  {r.observations ? (
                    <InfoTag text="Info" content={r.observations} />
                  ) : null}

                  {r._isUnsub && (
                    <span
                      className="mt-1 inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-100"
                      title={
                        r._unsubDate
                          ? `Désinscrit le ${fmtDateFR(r._unsubDate)}`
                          : "Désinscrit"
                      }
                    >
                      Désinscrit{" "}
                      {r._unsubDate ? `· ${fmtDateFR(r._unsubDate)}` : ""}
                    </span>
                  )}
                </td>

                <td className="px-3 py-3 text-sm">{r.discipline || "—"}</td>

                <td className="px-3 py-3 text-sm">
                  {r.departement_code || "—"}
                </td>

                <td className="px-3 py-3 text-sm tabular-nums">
                  {Number.isFinite(+r.nb_licencies) ? r.nb_licencies : "—"}
                </td>

                <td className="px-3 py-3 text-sm">
                  <div className="flex items-center gap-3">
                    {(r.contact_nom || r.fonction) && (
                      <span
                        title={`${r.contact_nom || ""}${
                          r.fonction ? ` — ${r.fonction}` : ""
                        }`}
                        className="inline-flex items-center"
                      >
                        <FaUser className="opacity-70" />
                      </span>
                    )}

                    {emails.length ? (
                      <a
                        href={`mailto:${emails.join(",")}`}
                        title={emails.join("\n")}
                        className="inline-flex items-center"
                      >
                        <FaEnvelope className="opacity-70" />
                      </a>
                    ) : null}

                    {r.telephone ? (
                      <a
                        href={`tel:${r.telephone}`}
                        title={r.telephone}
                        className="inline-flex items-center"
                      >
                        <FaPhone className="opacity-70" />
                      </a>
                    ) : null}

                    {!r.contact_nom &&
                    !r.fonction &&
                    emails.length === 0 &&
                    !r.telephone
                      ? "—"
                      : null}
                  </div>
                </td>

                <td className="px-3 py-3">
                  <InlineStatutSelect
                    value={r.statut || ""}
                    onChange={(v) => onStatusChange(r, v)}
                  />
                </td>

                <td className="px-3 py-3 text-sm whitespace-nowrap">
                  {fmtDateFR(r.date_contact) || "—"}
                </td>

                <td className="px-3 py-3 text-sm">
                  {r.site_url ? (
                    <a
                      href={toAbsolute(r.site_url)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-blue-600 hover:underline"
                    >
                      <FaGlobe /> Site
                    </a>
                  ) : (
                    "—"
                  )}

                  <div className="text-xs text-slate-500 mt-1">
                    {r.site_status || ""}
                  </div>
                </td>

                <td className="px-3 py-3">
                  <div className="flex flex-wrap justify-center gap-2">
                    <button
                      type="button"
                      className="px-3 py-1.5 rounded-md bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 text-indigo-700 dark:text-indigo-200 text-sm disabled:opacity-50"
                      onClick={() => onQuickCopy && onQuickCopy(r)}
                      title={
                        quickCopyDisabled
                          ? "Email absent ou contact désinscrit"
                          : "Copier l'email et mettre la date + heure de contact à maintenant"
                      }
                      disabled={quickCopyDisabled}
                    >
                      <FaCopy />
                    </button>

                    <button
                      type="button"
                      className="px-3 py-1.5 rounded-md bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-sm"
                      onClick={() => onEdit(r)}
                      title="Éditer"
                    >
                      <FaEdit />
                    </button>

                    <button
                      type="button"
                      className="px-3 py-1.5 rounded-md bg-rose-50 dark:bg-rose-900/30 hover:bg-rose-100 dark:hover:bg-rose-900/50 text-rose-700 dark:text-rose-200 text-sm"
                      onClick={() => onDelete(r.id)}
                      title="Supprimer"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}

          {!rows.length && (
            <tr>
              <td
                colSpan={10}
                className="px-4 py-10 text-center text-sm text-slate-500"
              >
                Aucun prospect ne correspond aux filtres.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function InlineStatutSelect({ value, onChange }) {
  const options = [
    "",
    "A contacter",
    "Intérêt",
    "Prospect",
    "Prospect +",
    "Client",
    "Perdu",
    "Désinscrit",
    "Mail invalide",
  ];

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="px-3 py-2 text-sm rounded-md bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
    >
      {options.map((o) => (
        <option key={o || "null"} value={o}>
          {o || "—"}
        </option>
      ))}
    </select>
  );
}

function SkeletonTable() {
  return (
    <div className="p-4 space-y-3">
      {Array.from({ length: 8 }).map((_, i) => (
        <div
          key={i}
          className="h-14 rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse"
        />
      ))}
    </div>
  );
}